import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useHomepageHighlights, useFeaturedPackages } from '../hooks/useCMS';
import { HomepageHighlight, Package } from '../types/cms';
import MiniSlider from '../components/MiniSlider'; // 👈 slider nuevo
import SafeImage from '../components/SafeImage';
import SEOHead from '../components/SEOHead';
import { SITE_CONFIG, getCanonicalUrl } from '../lib/seo';

function Home() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    data: highlightsData,
    loading: highlightsLoading,
    error: highlightsError,
  } = useHomepageHighlights();

  const {
    data: packagesData,
    loading: packagesLoading,
    error: packagesError,
  } = useFeaturedPackages();

  const [sliderHighlights, setSliderHighlights] = useState<HomepageHighlight[]>(
    []
  );
  const [regularHighlights, setRegularHighlights] = useState<
    HomepageHighlight[]
  >([]);

  /* clasificar al recibir datos */
  useEffect(() => {
    if (!highlightsData?.homepageHighlights) return;
    const sliders = highlightsData.homepageHighlights.filter(
      (h) => h.type.toLowerCase() === 'slider'
    );
    const regular = highlightsData.homepageHighlights.filter(
      (h) => h.type.toLowerCase() !== 'slider'
    );
    setSliderHighlights(sliders);
    setRegularHighlights(regular);
  }, [highlightsData]);

  if (highlightsLoading || packagesLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (highlightsError || packagesError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Error al cargar el contenido. Por favor, intenta de nuevo más tarde.
        </Alert>
      </Container>
    );
  }

  const packages = packagesData?.packages || [];

  /* --------- componente tarjeta highlight y paquete (sin cambios) --------- */
  const HighlightCard = ({ highlight }: { highlight: HomepageHighlight }) => {
    const img =
      highlight.image?.publicUrl ||
      highlight.relatedPackage?.imageCover?.publicUrl ||
      highlight.relatedPost?.coverImage?.publicUrl ||
      highlight.relatedDestination?.imageCover?.publicUrl ||
      '';
    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          },
        }}
      >
        <SafeImage
          src={img}
          alt={highlight.title}
          height={200}
          sx={{ width: '100%', objectFit: 'cover' }}
        />
        <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 2.5 } }}>
          <Chip
            label={highlight.type}
            size="small"
            color="primary"
            sx={{ mb: 1.5, fontWeight: 500 }}
          />
          <Typography
            gutterBottom
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, lineHeight: 1.3 }}
          >
            {highlight.title}
          </Typography>
          {highlight.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, lineHeight: 1.5 }}
            >
              {highlight.description}
            </Typography>
          )}
          {highlight.ctaText && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              href={
                highlight.ctaUrl ||
                `/paquetes/${highlight.relatedPackage?.slug ?? ''}` ||
                '#'
              }
              sx={{ mt: 'auto', fontWeight: 500 }}
            >
              {highlight.ctaText}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  const PackageCard = ({ pkg }: { pkg: Package }) => (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        },
      }}
    >
      <SafeImage
        src={pkg.imageCover?.publicUrl}
        alt={pkg.title}
        height={200}
        sx={{ width: '100%', objectFit: 'cover' }}
      />
      <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 2.5 } }}>
        <Typography
          gutterBottom
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, lineHeight: 1.3 }}
        >
          {pkg.title}
        </Typography>
        {pkg.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, lineHeight: 1.5 }}
          >
            {pkg.description}
          </Typography>
        )}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
          }}
        >
          {pkg.destinations?.slice(0, 3).map((d) => (
            <Chip
              key={d.id}
              label={d.title}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 'auto',
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            Desde USD {pkg.priceFrom?.toLocaleString()?.replace(/,/g, '.')}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            href={`/paquetes/${pkg.slug}`}
            sx={{ fontWeight: 500 }}
          >
            Ver más
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  /* ------------------------------ render ------------------------------- */
  // Obtener imagen destacada para OG
  const ogImage = sliderHighlights.length > 0 && sliderHighlights[0].image?.publicUrl
    ? sliderHighlights[0].image.publicUrl
    : undefined;

  // Schema JSON-LD para Organization
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    name: SITE_CONFIG.name,
    description: SITE_CONFIG.description,
    url: SITE_CONFIG.url,
    logo: `${SITE_CONFIG.url}${SITE_CONFIG.defaultImage}`,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'UY',
    },
    areaServed: ['UY', 'AR', 'BR', 'CL', 'PY'],
    priceRange: '$$',
    sameAs: [
      // TODO: Agregar redes sociales cuando estén disponibles
    ],
  };

  return (
    <>
      <SEOHead
        title="Paquetes de Viaje y Excursiones desde Uruguay"
        description="Agencia de viajes uruguaya especializada en excursiones, paquetes grupales y viajes en bus. Viajes accesibles con financiación a Argentina, Brasil y todo Latinoamérica. Desde Montevideo e Interior del país."
        keywords={[
          'viajes desde Uruguay',
          'paquetes turísticos',
          'excursiones',
          'viajes en bus',
          'financiación de viajes',
          'viajes desde Montevideo',
          'viajes desde Interior Uruguay',
          'paquetes grupales',
          'viajes a Argentina',
          'viajes a Brasil',
        ]}
        image={ogImage}
        url="/"
        type="website"
        schema={organizationSchema}
      />
      <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
        <Container
          maxWidth="lg"
          sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 4, md: 6 } }}
        >
        {/* slider principal */}
        <MiniSlider slides={sliderHighlights} />

        {/* otros destacados */}
        {regularHighlights.length > 0 && (
          <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                mb: { xs: 2, md: 3 },
                color: 'text.primary',
              }}
            >
              Otros Destacados
            </Typography>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {regularHighlights.map((h) => (
                <Grid item xs={12} sm={6} md={4} key={h.id}>
                  <HighlightCard highlight={h} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* paquetes destacados */}
        {packages.length > 0 && (
          <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                mb: { xs: 2, md: 3 },
                color: 'text.primary',
              }}
            >
              Paquetes Destacados
            </Typography>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {packages.slice(0, 6).map((p) => (
                <Grid item xs={12} sm={6} md={4} key={p.id}>
                  <PackageCard pkg={p} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
    </>
  );
}

export default Home;
